/**
 * Aktivite feed metin şablonları ve canlı popup uygunluk kuralları (API ui-config ile uyumlu).
 * Item sistemi kaldırıldı — item_equip + activation_item event tipleri legacy (yeni üretilmez).
 */

import { RARITY_LABELS_TR, rarityKey } from './collectionUi';
import { t } from '../locales';

const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary', 'mythic'];

export const ACTIVITY_FEED_KNOWN_EVENT_TYPES = [
  'activation_character',
  'rare_drop',
  'star_progress',
  'collection_score_milestone',
  'achievement_unlocked',
];

function defaultTemplatesObject() {
  return {
    rare_drop: t('activityFeed.templates.rareDrop'),
    collection_score_milestone: t('activityFeed.templates.scoreMilestone'),
    star_progress: t('activityFeed.templates.starProgress'),
    activation_character: t('activityFeed.templates.activationCharacter'),
    achievement_unlocked: t('activityFeed.templates.achievementUnlocked'),
  };
}

/** Sunucu varsayılanlarıyla aynı (şablon yok / bozuksa yedek) — getter ile güncel dil */
export const ACTIVITY_FEED_DEFAULT_TEMPLATES = {
  get rare_drop() {
    return t('activityFeed.templates.rareDrop');
  },
  get collection_score_milestone() {
    return t('activityFeed.templates.scoreMilestone');
  },
  get star_progress() {
    return t('activityFeed.templates.starProgress');
  },
  get activation_character() {
    return t('activityFeed.templates.activationCharacter');
  },
  get achievement_unlocked() {
    return t('activityFeed.templates.achievementUnlocked');
  },
};

export function feedRarityRank(r) {
  const x = (r || 'common').toLowerCase();
  const i = RARITY_ORDER.indexOf(x);
  return i >= 0 ? i : 0;
}

export function rarityMeetsMinimum(assetRarity, minimum) {
  if (assetRarity == null || String(assetRarity).trim() === '') return true;
  return feedRarityRank(assetRarity) >= feedRarityRank(minimum);
}

function fallbackFeedMessage(ev) {
  if (!ev || !ev.event_type) return t('activityFeed.fallbackGeneric');
  const u = ev.user_display_name || t('activityFeed.collectorDefault');
  const asset = ev.asset_name || '—';
  switch (ev.event_type) {
    case 'activation_character':
      return t('activityFeed.templates.activationCharacter').replace(/\{user\}/g, u).replace(/\{asset\}/g, asset);
    case 'rare_drop':
      return `${u} ${asset} ile nadir bir keşif yaptı`;
    case 'star_progress':
      return t('activityFeed.templates.starProgress')
        .replace(/\{user\}/g, u)
        .replace(/\{character\}/g, asset)
        .replace(/\{star_level\}/g, String(ev.star_level ?? ''));
    case 'collection_score_milestone':
      return t('activityFeed.templates.scoreMilestone')
        .replace(/\{user\}/g, u)
        .replace(/\{score\}/g, String(ev.collection_score ?? ''));
    case 'achievement_unlocked': {
      const ach = ev.asset_name || '—';
      return t('activityFeed.templates.achievementUnlocked').replace(/\{user\}/g, u).replace(/\{achievement\}/g, ach);
    }
    default:
      return t('activityFeed.fallbackDefault').replace(/\{user\}/g, u);
  }
}

/**
 * @param {object} ev — API olay satırı
 * @param {Record<string,string>} templates — ui-config.message_templates
 */
export function formatActivityFeedMessage(ev, templates) {
  const merged = { ...defaultTemplatesObject(), ...(templates && typeof templates === 'object' ? templates : {}) };
  const type = ev?.event_type;
  const tpl = merged[type];
  if (!tpl || typeof tpl !== 'string' || !tpl.trim()) {
    return fallbackFeedMessage(ev);
  }
  try {
    const user = ev.user_display_name || t('activityFeed.collectorDefault');
    const asset = ev.asset_name || '—';
    const score = ev.collection_score != null ? String(ev.collection_score) : '';
    const star = ev.star_level != null ? String(ev.star_level) : '';
    const character = (ev.character_name && String(ev.character_name).trim()) || asset;
    const rk = rarityKey(ev.asset_rarity);
    const rarity = RARITY_LABELS_TR[rk] || String(ev.asset_rarity || 'common').toLowerCase();
    const achievement = (ev.metadata && ev.metadata.achievement_title) || asset;
    let out = tpl
      .replace(/\{user\}/g, user)
      .replace(/\{asset\}/g, asset)
      .replace(/\{achievement\}/g, achievement)
      .replace(/\{score\}/g, score)
      .replace(/\{star\}/g, star)
      .replace(/\{star_level\}/g, star)
      .replace(/\{character\}/g, character)
      .replace(/\{rarity\}/g, rarity);
    out = out.trim();
    if (out) return out.slice(0, 500);
  } catch {
    /* yedek */
  }
  return fallbackFeedMessage(ev);
}

/** API GET /activity-feed/ui-config yanıtı (veya alt küme) */
export function defaultActivityFeedUiConfig() {
  return {
    /** Sunucu ayarı değişince artar; istemci şablon/flag senkronu için karşılaştırılabilir */
    config_version: 0,
    message_templates: { ...defaultTemplatesObject() },
    show_thumbnails: true,
    show_rarity_badges: true,
    show_relative_time: true,
    enable_popup_glow: true,
    live_popup_enabled: true,
    public_feed_enabled: true,
    popup_event_types: ['rare_drop', 'star_progress', 'collection_score_milestone', 'achievement_unlocked'],
    min_popup_rarity: 'epic',
    popup_min_star_level: 2,
    popup_min_milestone_score: 500,
  };
}

/**
 * @param {object} ev
 * @param {object} cfg — ui-config + isteğe bağlı tam ayarlar
 */
export function qualifiesForLivePopup(ev, cfg) {
  if (!ev?.event_type) return false;
  const c = cfg && typeof cfg === 'object' ? cfg : defaultActivityFeedUiConfig();
  if (c.live_popup_enabled === false) return false;
  const types = Array.isArray(c.popup_event_types) ? c.popup_event_types : [];
  if (!types.includes(ev.event_type)) return false;
  const minR = c.min_popup_rarity || 'epic';
  const minStar = Number(c.popup_min_star_level) || 2;
  const minScore = Number(c.popup_min_milestone_score) || 500;
  const rk = (ev.asset_rarity || 'common').toLowerCase();
  switch (ev.event_type) {
    case 'rare_drop':
      return rarityMeetsMinimum(rk, minR);
    case 'star_progress':
      return rarityMeetsMinimum(rk, minR) && (Number(ev.star_level) || 0) >= minStar;
    case 'collection_score_milestone':
      return (Number(ev.collection_score) || 0) >= minScore;
    case 'achievement_unlocked':
      return true;
    default:
      return false;
  }
}
